import { lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { Route, Routes } from 'react-router-dom';
import { Toaster } from '@/components/common/Toaster';
import { TermsReconsentGate } from '@/components/common/TermsReconsentGate';
import { AppLayout } from '@/layouts/AppLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { ViewerAdaptiveLayout } from '@/layouts/ViewerAdaptiveLayout';
import { SharedProfileLayout } from '@/layouts/SharedProfileLayout';
import { ProtectedRoute } from '@/router/ProtectedRoute';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { TwoFactorPage } from '@/pages/auth/TwoFactorPage';
import { TwoFactorSetupPage } from '@/pages/auth/TwoFactorSetupPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { InviteSignupPage } from '@/pages/auth/InviteSignupPage';
import { InviteVerifyOtpPage } from '@/pages/auth/InviteVerifyOtpPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { VerificationSuccessPage } from '@/pages/auth/VerificationSuccessPage';
import { TeamPermissionsPage } from '@/pages/team/TeamPermissionsPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { AccountPage } from '@/pages/account/AccountPage';
import { CreateStartupPage } from '@/pages/startups/CreateStartupPage';
import { StartupCreatedPage } from '@/pages/startups/StartupCreatedPage';
import { EntityProfilePage } from '@/pages/profile/EntityProfilePage';
import { StartupProfilePreviewPage } from '@/pages/profile/StartupProfilePreviewPage';
import { SharedProfileGatePage } from '@/pages/shared-profile/SharedProfileGatePage';
import { SharedProfileVerifyOtpPage } from '@/pages/shared-profile/SharedProfileVerifyOtpPage';
import { SharedProfileViewPage } from '@/pages/shared-profile/SharedProfileViewPage';
import { ViewAsEndedPage } from '@/pages/view-as/ViewAsEndedPage';
import { HomePage } from '@/pages/HomePage';
import { BenefitsPage } from '@/pages/BenefitsPage';
import { InvestorsPage } from '@/pages/InvestorsPage';
import { TrustedPartnersPublicPage } from '@/pages/TrustedPartnersPublicPage';
import { DueDiligencePlatformPage } from '@/pages/DueDiligencePlatformPage';
import { EventsPage } from '@/features/events/EventsPage';
import { EventDetailPage } from '@/features/events/EventDetailPage';
import { PartnerOffersConsolePage } from '@/features/partner-offers/PartnerOffersConsolePage';
import { TrustedPartnersPage } from '@/pages/trusted-partners/TrustedPartnersPage';
import { TrustedPartnersDirectoryPage } from '@/pages/trusted-partners/TrustedPartnersDirectoryPage';
import { TrustedPartnersPerksPage } from '@/pages/trusted-partners/TrustedPartnersPerksPage';
import { TrustedPartnerProfilePage } from '@/pages/trusted-partners/TrustedPartnerProfilePage';
import { OfferFormPage } from '@/features/partner-offers/OfferFormPage';
import { ResourcesPage } from '@/features/resources/ResourcesPage';
import { DataRoomHandoff } from '@/features/data-room/components/DataRoomHandoff';
import { FolderDetailPage } from '@/features/resources/FolderDetailPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { DataRoomPage } from '@/features/data-room/DataRoomPage';
import { IssueReportStartPage } from '@/features/data-room/IssueReportStartPage';
import { CheckoutReturnPage } from '@/features/billing/CheckoutReturnPage';
import { CheckoutReviewPage } from '@/features/billing/CheckoutReviewPage';
import { PurchasePage } from '@/features/billing/PurchasePage';
import { ROUTES } from '@/lib/constants/routes';
import { ROLES } from '@/lib/constants/roles';

// Super Admin console: separate lazy bundle (ADR FE-1).
const AdminApp = lazy(() => import('@/pages/admin/AdminApp'));

function PlaceholderPage({ name }: { name: string }) {
  return (
    <div className="text-slate flex h-64 items-center justify-center text-sm">
      {name} - coming soon
    </div>
  );
}

function App() {
  return (
    <>
      <Toaster />
      {/* Session-level, not per-route (MYN-33) - renders nothing unless the
        session user needs to re-accept the Terms, regardless of which
        authenticated route is active. */}
      <TermsReconsentGate />
      <Routes>
        {/* Public / cold-arrival routes - header + footer, no auth */}
        <Route element={<PublicLayout />}>
          <Route path={ROUTES.HOME} element={<HomePage />} />
          <Route path={ROUTES.BENEFITS} element={<BenefitsPage />} />
          {/* Public pages not yet built (MYN-211 CTA-routing fix) - their own
            build ticket replaces the placeholder with the real page. */}
          <Route path={ROUTES.ABOUT} element={<PlaceholderPage name="About" />} />
          <Route path={ROUTES.DUE_DILIGENCE_PLATFORM} element={<DueDiligencePlatformPage />} />
          <Route path={ROUTES.INVESTORS} element={<InvestorsPage />} />
          <Route path={ROUTES.PARTNERS} element={<TrustedPartnersPublicPage />} />
          <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
          {/* Step 2 of login. Not a cold-arrival URL (ADR FE-1): the page
            itself redirects to /login when the step-1 credential is absent. */}
          <Route path={ROUTES.AUTH.TWO_FACTOR} element={<TwoFactorPage />} />
          <Route path={ROUTES.AUTH.TWO_FACTOR_SETUP} element={<TwoFactorSetupPage />} />
          <Route path={ROUTES.AUTH.REGISTER} element={<SignupPage />} />
          <Route path={ROUTES.AUTH.INVITE_SIGNUP} element={<InviteSignupPage />} />
          <Route path={ROUTES.AUTH.INVITE_VERIFY_OTP} element={<InviteVerifyOtpPage />} />
          <Route path={ROUTES.AUTH.VERIFY_EMAIL} element={<VerifyEmailPage />} />
          <Route path={ROUTES.AUTH.EMAIL_VERIFIED} element={<VerificationSuccessPage />} />
          <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        </Route>

        {/* Public Shared Startup Profile verification screens (MYN-58) - flat,
          deep-linked, reachable cold with no session (ADR FE-1). Own layout
          with the minimal marketing header (no Home/Sign In/Get Started), per
          design - an external recipient has no account to sign into. */}
        <Route element={<SharedProfileLayout />}>
          <Route path={ROUTES.SHARED_PROFILE} element={<SharedProfileGatePage />} />
          <Route path={ROUTES.SHARED_PROFILE_VERIFY_OTP} element={<SharedProfileVerifyOtpPage />} />
        </Route>

        {/* Shared Startup Profile read-only view (MYN-41/58) - standalone, no
          layout wrapper (minimal chrome: logo + Browse Public Pages only). */}
        <Route path={ROUTES.SHARED_PROFILE_VIEW} element={<SharedProfileViewPage />} />

        {/* A view-as tab once its session ended (MYN-335) - standalone, no shell.
          With a token, /view-as never renders: main.tsx consumes it first. */}
        <Route path={ROUTES.VIEW_AS} element={<ViewAsEndedPage />} />

        {/* Event detail - ONE url for both audiences, so the public Home events
          section's "View Details" opens the event itself instead of bouncing an
          anonymous visitor to login. The shell follows the viewer (member nav
          when signed in, marketing header when not); the page withholds the
          member-only actions. The read behind it, GET /v1/events/:id, is already
          public. */}
        <Route element={<ViewerAdaptiveLayout />}>
          <Route path={ROUTES.EVENT_DETAIL} element={<EventDetailPage />} />
          {/* The Issue Report CTA journey (FR-204): it must work logged out, and
            a signed-in user keeps their own header on it (FR-226). */}
          <Route path={ROUTES.ISSUE_REPORT_START} element={<IssueReportStartPage />} />
        </Route>

        {/* Authenticated area */}
        <Route element={<ProtectedRoute />}>
          {/* One adaptive shell for every persona */}
          <Route element={<AppLayout />}>
            <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />

            {/* Individual person account */}
            <Route path={ROUTES.ACCOUNT} element={<AccountPage />} />

            {/* Create-startup flow (MYN-46) - any authenticated user; the nav CTA
              surfaces it only for individuals without an active membership.
              requiresWorkEmail blocks a personal/disposable account email -
              enforced authoritatively server-side too (StartupsService). */}
            <Route element={<ProtectedRoute requiresWorkEmail />}>
              <Route path={ROUTES.CREATE_STARTUP} element={<CreateStartupPage />} />
            </Route>
            <Route path={ROUTES.STARTUP_CREATED} element={<StartupCreatedPage />} />

            {/* Owned entity workspace pages - persona-prefixed + role-guarded.
              One EntityProfilePage backs all three; the split is in the guard. */}
            <Route
              element={
                <ProtectedRoute allowedRoles={[ROLES.STARTUP_ADMIN, ROLES.STARTUP_MEMBER]} />
              }
            >
              <Route path={ROUTES.STARTUP_PROFILE} element={<EntityProfilePage />} />
              <Route
                path={ROUTES.STARTUP_PROFILE_PREVIEW}
                element={<StartupProfilePreviewPage />}
              />
            </Route>
            {/* Team Access (MYN-43). Unlike the earlier permission-gated
              behavior, entry is NOT gated on team.manage/team.invite - any
              startup_admin/startup_member reaches the page; TeamPermissionsPage
              itself renders the read-only "view-only" variant for a member
              holding neither permission, matching the investor Team Access
              route just below. */}
            <Route
              element={
                <ProtectedRoute allowedRoles={[ROLES.STARTUP_ADMIN, ROLES.STARTUP_MEMBER]} />
              }
            >
              <Route path={ROUTES.STARTUP_TEAM} element={<TeamPermissionsPage />} />
            </Route>
            <Route
              element={
                <ProtectedRoute allowedRoles={[ROLES.INVESTOR_ADMIN, ROLES.INVESTOR_MEMBER]} />
              }
            >
              <Route path={ROUTES.INVESTOR_PROFILE} element={<EntityProfilePage />} />
            </Route>
            {/* Team Access for the investor persona. Entry is identical to
              STARTUP_TEAM above - neither is gated on team.manage/team.invite;
              any investor_admin/investor_member reaches the page.
              TeamPermissionsPage itself renders the read-only "view-only"
              variant for a member holding neither permission. */}
            <Route
              element={
                <ProtectedRoute allowedRoles={[ROLES.INVESTOR_ADMIN, ROLES.INVESTOR_MEMBER]} />
              }
            >
              <Route path={ROUTES.INVESTOR_TEAM} element={<TeamPermissionsPage />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[ROLES.TRUSTED_PARTNER_ADMIN, ROLES.TRUSTED_PARTNER_MEMBER]}
                />
              }
            >
              <Route path={ROUTES.PARTNER_PROFILE} element={<EntityProfilePage />} />
            </Route>
            {/* Team Access for the trusted partner persona (MYN-102/104). Entry
              is identical to STARTUP_TEAM/INVESTOR_TEAM above - TEAM_PAGE_FEATURES
              already has a trusted_partner entry, so this reuses the same
              TeamPermissionsPage with no page-level changes. */}
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[ROLES.TRUSTED_PARTNER_ADMIN, ROLES.TRUSTED_PARTNER_MEMBER]}
                />
              }
            >
              <Route path={ROUTES.PARTNER_TEAM} element={<TeamPermissionsPage />} />
            </Route>
            {/* Perks & Offers console (FR-29, MYN-104) - the Trusted Partner's own
              self-service offer management, plus the create/edit offer form.
              Trusted-partner-only. The member-facing Perks page (Individual/
              startup members viewing live offers, gated server-side by
              `/v1/perks`) is a separate, not-yet-built FE surface - out of
              this ticket's frontend scope. */}
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[ROLES.TRUSTED_PARTNER_ADMIN, ROLES.TRUSTED_PARTNER_MEMBER]}
                />
              }
            >
              <Route path={ROUTES.PARTNER_OFFERS} element={<PartnerOffersConsolePage />} />
              <Route path={ROUTES.PARTNER_OFFER_NEW} element={<OfferFormPage />} />
              <Route path={ROUTES.PARTNER_OFFER_EDIT} element={<OfferFormPage />} />
            </Route>

            {/* Shared in-app pages */}
            <Route path={ROUTES.EVENTS} element={<EventsPage />} />
            <Route path={ROUTES.RESOURCES} element={<ResourcesPage />} />
            <Route path={ROUTES.RESOURCE_FOLDER} element={<FolderDetailPage />} />
            {/* Trusted Partners page, directory and profile (FR-111): every logged-in
              persona (A-90). The static `/directory` segment outranks `:entityId`. */}
            <Route path={ROUTES.TRUSTED_PARTNERS} element={<TrustedPartnersPage />} />
            <Route
              path={ROUTES.TRUSTED_PARTNERS_DIRECTORY}
              element={<TrustedPartnersDirectoryPage />}
            />
            <Route path={ROUTES.TRUSTED_PARTNERS_PERKS} element={<TrustedPartnersPerksPage />} />
            <Route path={ROUTES.TRUSTED_PARTNER_DETAIL} element={<TrustedPartnerProfilePage />} />
            {/* Billing (FR-07, FR-39, FR-41). STARTUP ADMIN ONLY, deliberately:
              §14 says a non-admin member does not see the route and a direct
              visit is refused rather than rendered read-only - a member cannot
              buy, cancel or change seats, so a read-only billing page would be
              a screen whose every control is inert. The checkout return is
              guarded the same way even though the member arrives from Stripe:
              only an admin could have started the session. */}
            <Route element={<ProtectedRoute allowedRoles={[ROLES.STARTUP_ADMIN]} />}>
              <Route path={ROUTES.BILLING} element={<BillingPage />} />
              <Route path={ROUTES.BILLING_PURCHASE} element={<PurchasePage />} />
              <Route path={ROUTES.BILLING_REVIEW} element={<CheckoutReviewPage />} />
              <Route path={ROUTES.BILLING_CHECKOUT_RETURN} element={<CheckoutReturnPage />} />
            </Route>
            {/* Data-room SSO handoff (MYN-164). Guarded to the startup roles
              that can hold an entitlement, so the route is no more reachable
              than the launch affordance that leads to it. */}
            <Route
              element={
                <ProtectedRoute allowedRoles={[ROLES.STARTUP_ADMIN, ROLES.STARTUP_MEMBER]} />
              }
            >
              <Route path={ROUTES.DATA_ROOM_HANDOFF} element={<DataRoomHandoff />} />
            </Route>
            {/* The Legal Command Center (MYN-331, FR-299): every logged-in
              user. Its top decides what each one sees; the launch above stays
              startup-only. */}
            <Route path={ROUTES.DATA_ROOM} element={<DataRoomPage />} />
          </Route>

          {/* Admin console - own bundle + guard, outside the AppLayout chrome */}
          <Route element={<ProtectedRoute requiresAdmin />}>
            <Route
              path={`${ROUTES.ADMIN}/*`}
              element={
                <Suspense fallback={<div className="text-slate p-8">Loading...</div>}>
                  <AdminApp />
                </Suspense>
              }
            />
          </Route>
        </Route>

        {/* 404 - standalone, no layout wrapper */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

export default Sentry.withErrorBoundary(App, {
  fallback: <p>An unexpected error occurred. Please refresh the page.</p>,
});
